import type {
	Batch,
	DiningTable,
	Kot,
	LiveBoard,
	LiveOrder,
	Order,
	OrderItem,
	TablesList,
} from "@luckymenu/core";
import { type CancelItemEntry, type OutboxEntry, type SendEntry, sendsFor } from "./outbox";

/**
 * The order screen (ux-design.md, Order): items by batch, what is still on
 * its way, and what staff may do with it. Pure functions over
 * `staff.ordering.getOrder` and the outbox, shared by both staff apps.
 */

export type BatchView = {
	batch: Batch;
	/** Every line, removed and cancelled ones included (struck through). */
	items: OrderItem[];
	/** Active units. */
	activeCount: number;
	/** The batch's KOTs (`new` ones), oldest first. */
	kots: Kot[];
};

export type OrderView = {
	order: Order;
	/** Items can be added, cancelled, moved and merged: the order is open. */
	editable: boolean;
	batches: BatchView[];
	/** Lines sent from this device that the server has not confirmed yet. */
	sending: SendEntry[];
	/** Cancels from this device not confirmed yet (waiting, on their way, or refused), by order item. */
	cancels: ReadonlyMap<string, CancelItemEntry>;
	/** Active units on the order. */
	itemCount: number;
	/** Cancel KOTs, oldest first. */
	cancelKots: Kot[];
};

export function orderView(order: Order, outbox: readonly OutboxEntry[] = []): OrderView {
	const batches = [...order.batches]
		.filter((b) => b.status === "accepted")
		.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
		.map((batch) => ({
			batch,
			items: batch.items,
			activeCount: batch.items
				.filter((i) => i.status === "active")
				.reduce((n, i) => n + i.quantity, 0),
			kots: order.kots
				.filter((k) => k.batchId === batch.id && k.kind === "new")
				.sort((a, b) => a.kotNo - b.kotNo),
		}));
	return {
		order,
		editable: order.status === "open",
		batches,
		sending: sendsFor(outbox, order.id),
		cancels: new Map(
			outbox
				.filter((e): e is CancelItemEntry => e.kind === "cancelItem" && e.orderId === order.id)
				.map((e) => [e.input.orderItemId, e]),
		),
		itemCount: batches.reduce((n, b) => n + b.activeCount, 0),
		cancelKots: order.kots.filter((k) => k.kind === "cancel").sort((a, b) => a.kotNo - b.kotNo),
	};
}

/** "T4" or null for a takeaway: how the order is named with `orderNo`. */
export function orderTitle(order: Pick<Order, "type" | "tableLabel" | "orderNo">): {
	table: string | null;
	orderNo: number;
	takeaway: boolean;
} {
	return {
		table: order.type === "dine_in" ? order.tableLabel : null,
		orderNo: order.orderNo,
		takeaway: order.type === "takeaway",
	};
}

/** Whether a line can be cancelled: active, on an open order, and no cancel of it on its way. */
export function canCancelItem(view: OrderView, item: OrderItem): boolean {
	return view.editable && item.status === "active" && !view.cancels.has(item.id);
}

/** Tables an order can move to: in use, and with no open or billed order. */
export function moveTargets(
	tables: TablesList,
	board: LiveBoard,
	order: Pick<Order, "id" | "tableId">,
): DiningTable[] {
	const taken = new Set(board.orders.map((o) => o.tableId).filter(Boolean));
	return tables.tables.filter((t) => t.isActive && t.id !== order.tableId && !taken.has(t.id));
}

/** Other open orders that can be merged into this one (OP-10). */
export function mergeCandidates(board: LiveBoard, orderId: string): LiveOrder[] {
	return board.orders.filter((o) => o.status === "open" && o.id !== orderId);
}

/** The open order at a table, if any: where a tap on the table goes. */
export function openOrderAt(board: LiveBoard, tableId: string): LiveOrder | null {
	return board.orders.find((o) => o.tableId === tableId) ?? null;
}
